"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { cloneElement, isValidElement, useEffect, useId, useState, useTransition } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { checkSubdomainAction, tenantSignupStartAction } from "@/lib/platform-actions";
import { PLATFORM_SUBDOMAIN_SUFFIX } from "@/lib/platform-brand";

// Mirrors the kudu-api garde validator for the signup `start` endpoint.
const schema = z.object({
  displayName: z.string().min(1, "Required.").max(120, "Too long."),
  slug: z
    .string()
    .min(3, "At least 3 characters.")
    .max(40, "Too long.")
    .regex(
      /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
      "Lowercase letters, numbers, and single hyphens only.",
    ),
  email: z.string().email("Enter a valid email."),
  password: z
    .string()
    .min(12, "Use at least 12 characters.")
    .max(256, "Too long.")
    .regex(/[A-Z]/, "Add an uppercase letter.")
    .regex(/[a-z]/, "Add a lowercase letter.")
    .regex(/\d/, "Add a number."),
});

type FormShape = z.input<typeof schema>;
type SlugState = "idle" | "checking" | "available" | "taken" | "invalid";

const SLUG_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

export function SignupForm({ planCode }: { planCode: string }) {
  const [serverErr, setServerErr] = useState<string | null>(null);
  const [slugState, setSlugState] = useState<SlugState>("idle");
  const [pending, startTransition] = useTransition();
  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<FormShape>({ resolver: zodResolver(schema) });

  const slug = watch("slug");

  // Debounced live availability check (the API re-checks authoritatively).
  useEffect(() => {
    const value = (slug ?? "").trim().toLowerCase();
    if (!value) {
      setSlugState("idle");
      return;
    }
    if (value.length < 3 || value.length > 40 || !SLUG_RE.test(value)) {
      setSlugState("invalid");
      return;
    }
    setSlugState("checking");
    let cancelled = false;
    const t = setTimeout(async () => {
      const ok = await checkSubdomainAction(value);
      if (!cancelled) setSlugState(ok ? "available" : "taken");
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [slug]);

  return (
    <form
      onSubmit={handleSubmit((data) =>
        startTransition(async () => {
          setServerErr(null);
          const res = await tenantSignupStartAction({
            displayName: data.displayName,
            slug: data.slug,
            email: data.email,
            password: data.password,
            planCode,
          });
          if (res.ok) {
            // External redirect to Stripe-hosted Checkout.
            window.location.href = res.checkoutUrl;
            return;
          }
          if (res.field === "slug") setError("slug", { message: res.message });
          else if (res.field === "email") setError("email", { message: res.message });
          else setServerErr(res.message);
        }),
      )}
      noValidate
      className="space-y-5"
    >
      {serverErr && (
        <div
          role="alert"
          className="border px-3 py-2 text-[13px]"
          style={{
            borderColor: "rgba(177,69,69,0.4)",
            background: "rgba(177,69,69,0.06)",
            color: "var(--color-danger)",
          }}
        >
          {serverErr}
        </div>
      )}

      <Field label="Store name" error={errors.displayName?.message}>
        <input
          type="text"
          autoComplete="organization"
          {...register("displayName")}
          className="h-10 w-full border bg-white px-3.5"
          style={{ borderColor: "var(--color-line)" }}
        />
      </Field>

      {/* Subdomain with inline .oluflow.com suffix + live availability. */}
      <label className="block">
        <span className="mb-1.5 block text-[12px] font-semibold text-ink">Subdomain</span>
        <span
          className="flex h-10 items-center border bg-white"
          style={{ borderColor: "var(--color-line)" }}
        >
          <input
            type="text"
            autoCapitalize="none"
            autoComplete="off"
            spellCheck={false}
            placeholder="your-store"
            aria-invalid={errors.slug || slugState === "taken" ? true : undefined}
            {...register("slug")}
            className="h-full min-w-0 flex-1 bg-transparent px-3.5 outline-none"
          />
          <span className="whitespace-nowrap px-3 text-[13px] text-[color:var(--color-muted)]">
            .{PLATFORM_SUBDOMAIN_SUFFIX}
          </span>
        </span>
        {errors.slug?.message ? (
          <span role="alert" className="mt-1.5 block text-[12px] text-[color:var(--color-danger)]">
            {errors.slug.message}
          </span>
        ) : slugState === "checking" ? (
          <span className="mt-1.5 block text-[12px] text-[color:var(--color-muted)]">
            Checking availability…
          </span>
        ) : slugState === "available" ? (
          <span className="mt-1.5 block text-[12px] text-ink">✓ Available</span>
        ) : slugState === "taken" ? (
          <span role="alert" className="mt-1.5 block text-[12px] text-[color:var(--color-danger)]">
            That subdomain is taken.
          </span>
        ) : slugState === "invalid" ? (
          <span className="mt-1.5 block text-[12px] text-[color:var(--color-muted)]">
            Lowercase letters, numbers, and single hyphens.
          </span>
        ) : null}
      </label>

      <Field label="Email" error={errors.email?.message}>
        <input
          type="email"
          autoComplete="email"
          {...register("email")}
          className="h-10 w-full border bg-white px-3.5"
          style={{ borderColor: "var(--color-line)" }}
        />
      </Field>

      <Field label="Password" error={errors.password?.message}>
        <PasswordInput autoComplete="new-password" {...register("password")} />
      </Field>

      <button
        type="submit"
        disabled={pending || slugState === "taken"}
        className="eyebrow-strong w-full bg-[color:var(--color-ink)] px-[30px] py-[14px] text-white transition-colors hover:bg-[color:var(--color-accent)] disabled:opacity-50"
      >
        {pending ? "Starting checkout…" : "Continue to payment"}
      </button>
      <p className="text-center text-[12px] text-[color:var(--color-muted)]">
        You&rsquo;ll enter payment details on the next step.
      </p>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  const errorId = useId();
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: input is nested as children
    <label className="block">
      <span className="mb-1.5 block text-[12px] font-semibold text-ink">{label}</span>
      {isValidElement(children)
        ? cloneElement(children as React.ReactElement<Record<string, unknown>>, {
            "aria-invalid": error ? true : undefined,
            "aria-describedby": error ? errorId : undefined,
          })
        : children}
      {error && (
        <span
          id={errorId}
          role="alert"
          className="mt-1.5 block text-[12px] text-[color:var(--color-danger)]"
        >
          {error}
        </span>
      )}
    </label>
  );
}
